import { useEffect, useState } from 'react';

import type { AiQuotaState } from '~shared/stores/aiQuotaStore';
import type { AiQuotaBucket, BucketQuota } from '~shared/stores/aiQuotaStore';
import { getAiQuotaSnapshot, subscribe } from '~shared/stores/aiQuotaStore';
import type { QuotaBucket } from '~teacher/components/QuotaInlineMessage/types';

export type QuotaLevel = 'ok' | 'warning' | 'exceeded';

const WARNING_THRESHOLD = 0.8;
const EXCEEDED_THRESHOLD = 1;

/**
 * Maps UI-level QuotaBucket to backend AiQuotaBucket.
 * Only 'Generate' and 'Import' are mapped — Insights and QuestionAssist
 * don't show inline quota messages per the spec (SOC-5206).
 */
const BUCKET_MAP: Record<QuotaBucket, AiQuotaBucket> = {
  Generate: 'questions',
  Import: 'imports',
};

export function useAiQuotaWarning(uiBucket: QuotaBucket) {
  const [state, setState] = useState<AiQuotaState>(getAiQuotaSnapshot);

  useEffect(() => {
    const unsub = subscribe(() => setState(getAiQuotaSnapshot()));
    return unsub;
  }, []);

  const storeBucket = BUCKET_MAP[uiBucket];
  const quota: BucketQuota | null = state[storeBucket] ?? null;

  if (!quota) {
    return {
      level: 'ok' as const,
      quota: null,
      resetDate: null,
      percentUsed: 0,
    };
  }

  const used = quota.weeklyLimit - quota.weeklyRemaining;
  const percentUsed = quota.weeklyLimit > 0 ? used / quota.weeklyLimit : 0;

  let level: QuotaLevel = 'ok';
  if (percentUsed >= EXCEEDED_THRESHOLD) {
    level = 'exceeded';
  } else if (percentUsed >= WARNING_THRESHOLD) {
    level = 'warning';
  }

  const parsed = quota.weeklyResetAt ? new Date(quota.weeklyResetAt) : null;
  const resetDate = parsed && !Number.isNaN(parsed.getTime()) ? parsed : null;

  return { level, quota, resetDate, percentUsed: percentUsed * 100 };
}
